/** Return the selectable selling-plan options from the canonical form select. */
export function getSellingPlanOptions(select) {
  return Array.from(select?.options || []).filter(
    (option) => option.value && !option.disabled,
  );
}

/** Return the selected purchase mode without treating an invalid placeholder as one-time consent. */
export function getPurchaseMode(select) {
  if (!select || select.validity?.customError) return "unresolved";
  const selected = select.options?.[select.selectedIndex];
  if (!selected || selected.disabled) return "unresolved";
  return selected.value ? "subscription" : "one-time";
}

/** Apply an explicit customer choice to the canonical selling_plan select. */
export function choosePurchaseOption(select, mode, preferredPlan = "") {
  if (!select) return false;
  const options = Array.from(select.options || []);
  let nextIndex = -1;

  if (mode === "one-time") {
    nextIndex = options.findIndex(
      (option) => option.value === "" && !option.disabled,
    );
  } else if (mode === "subscription") {
    nextIndex = options.findIndex(
      (option) =>
        option.value === preferredPlan && option.value && !option.disabled,
    );
    if (nextIndex < 0)
      nextIndex = options.findIndex(
        (option) => option.value && !option.disabled,
      );
  }

  if (nextIndex < 0) return false;
  select.selectedIndex = nextIndex;
  select.required = false;
  select.setCustomValidity("");
  return true;
}

/** Return the next purchase choice for standard radio-group navigation keys. */
export function nextPurchaseChoiceIndex(currentIndex, count, key) {
  if (
    !Number.isInteger(currentIndex) ||
    currentIndex < 0 ||
    !Number.isInteger(count) ||
    count < 1
  )
    return -1;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowLeft" || key === "ArrowUp")
    return (currentIndex - 1 + count) % count;
  if (key === "ArrowRight" || key === "ArrowDown")
    return (currentIndex + 1) % count;
  return -1;
}

/** Preserve a valid purchase selection when Shopify returns a new variant fragment. */
export function preservePurchaseSelection(currentSelect, nextSelect) {
  if (!currentSelect || !nextSelect || currentSelect.validity?.customError)
    return false;
  const previous = currentSelect.value;
  const exists = Array.from(nextSelect.options || []).some(
    (option) => option.value === previous && !option.disabled,
  );
  if (!exists) return false;
  nextSelect.value = previous;
  return true;
}

/** Multiply a Shopify minor-unit amount by a valid product-form quantity. */
export function quantityTotal(amount, quantity) {
  const minorUnits = Number(amount);
  const count = Number(quantity);
  if (!Number.isFinite(minorUnits) || !Number.isInteger(count) || count < 1)
    return null;
  return minorUnits * count;
}

/**
 * Read the per-variant item count from a variant label's parenthetical, for
 * example "6-Pack (36-Bagels)" or "Single Pack (14 Slices)". Labels without a
 * leading number in parentheses return null, so no count is shown.
 */
export function unitCountFromLabel(label) {
  const groups = String(label || "").match(/\(([^()]*)\)/g);
  if (!groups) return null;
  const inner = groups[groups.length - 1].slice(1, -1);
  const match = /^\s*(\d+)[\s-]+([A-Za-z][A-Za-z' -]*?)\s*$/.exec(inner);
  if (!match) return null;
  const count = Number(match[1]);
  if (!Number.isInteger(count) || count < 1) return null;
  const noun = match[2].replace(/-/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  return {
    count,
    singular: count === 1 ? noun : noun.replace(/s$/, ""),
    plural: count === 1 ? `${noun}s` : noun,
  };
}

/** Read only verified contents emitted by the curated-pack Liquid map. */
export function packContentsFromData(value) {
  const [unit, encoded, extra] = String(value || "").split("^");
  if (extra || !["pack", "box", "duo"].includes(unit) || !encoded)
    return null;
  const components = encoded.split("~").map((entry) => {
    const [rawCount, singular, plural, extraField] = entry.split("|");
    const count = Number(rawCount);
    if (extraField || !Number.isSafeInteger(count) || count < 1 || !singular || !plural)
      return null;
    return { count, singular, plural };
  });
  return components.length && components.every(Boolean) ? { unit, components } : null;
}

export function packContentsLabel(contents, quantity = 1, summary = false) {
  const count = Number(quantity);
  if (!contents || !Number.isSafeInteger(count) || count < 1) return "";
  const prefix = summary
    ? `${count} ${count === 1 ? contents.unit : contents.unit === "box" ? "boxes" : `${contents.unit}s`}: `
    : "";
  const separator = contents.unit === "duo" ? " + " : ", ";
  return prefix + contents.components.map((component) => {
    const total = count * component.count;
    return `${total} ${total === 1 ? component.singular : component.plural}`;
  }).join(separator);
}

/**
 * Describe a quantity as what arrives, not as arithmetic: "1 loaf, 14 slices",
 * "3-pack: 3 loaves, 42 slices", "2 × 3-pack: 6 loaves, 84 slices". Confirm,
 * do not compute; sums in a buy box read as complexity.
 */
export function quantityDetail(quantity, details = {}) {
  const count = Number(quantity);
  if (!Number.isInteger(count) || count < 1) return "";
  const contents = packContentsFromData(details.packContents);
  if (contents) return packContentsLabel(contents, count, true);
  const packCount = Number(details.packCount);
  if (!Number.isInteger(packCount) || packCount < 1) {
    return details.variantLabel
      ? `${count} × ${details.variantLabel}`
      : `Quantity ${count}`;
  }
  const packs = (n) =>
    `${n} ${n === 1 ? details.unitNoun || "item" : details.unitPlural || "items"}`;
  let totals = packs(packCount * count);
  const units = unitCountFromLabel(details.variantLabel);
  if (units) {
    const total = units.count * count;
    totals += `, ${total} ${total === 1 ? units.singular : units.plural}`;
  }
  if (packCount > 1) return `${count > 1 ? `${count} × ` : ""}${packCount}-pack: ${totals}`;
  return totals;
}

/** Build the one authoritative quantity and total summary for the current choice. */
export function purchaseSummary(option, quantity, details = {}) {
  if (!option || !["one-time", "subscription"].includes(details.mode))
    return null;
  const count = Number(quantity);
  const totalCents = quantityTotal(option.dataset?.priceCents, count);
  if (totalCents == null) return null;
  const baseline = Number(details.baselineTotalCents);
  // A one-time order keeps the pack saving against singles. A subscription
  // names only the plan's own saving, the figure on the Subscribe card.
  const hasPackSavings = details.mode === "one-time"
    && Number.isSafeInteger(baseline) && baseline > Number(option.dataset.priceCents);

  return {
    detail: quantityDetail(count, details),
    totalCents: details.hasQuantityPriceBreaks ? null : totalCents,
    savingsCents: details.hasQuantityPriceBreaks || details.available === false ? null
      : hasPackSavings ? quantityTotal(baseline - Number(option.dataset.priceCents), count)
      : details.mode === "subscription" ? quantityTotal(option.dataset?.savingsCents, count) : null,
    includesPackSavings: hasPackSavings,
  };
}

/** The Subscribe card's badge: the plan's saving against this pack's one-time price, per delivery. */
export function subscriptionBadgeText(dataset = {}, template = "") {
  const amount = dataset.savingsLabel || "";
  if (!amount) return "";
  return (typeof template === "string" && template.includes("__AMOUNT__"))
    ? template.replace("__AMOUNT__", amount)
    : `Save ${amount} per delivery`;
}

/** The Subscribe card's heading names the plan's own whole-number rate. */
export function subscriptionHeadingText(dataset = {}, fallback = "") {
  const percent = Number(dataset.savingsPercent);
  if (Number.isFinite(percent) && percent > 0)
    return `Subscribe and save ${Math.round(percent)}%`;
  return fallback || "Subscribe and save";
}

/** "Deliver every 1 month" -> "month"; "Deliver every 6 weeks" -> "6 weeks". */
export function renewalFrequency(planName = "") {
  const interval = String(planName || "")
    .trim()
    .replace(/^deliver(y)? every\s+/i, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
  return interval.replace(/^1 /, "");
}

/** Fill the renewal sentence; an unknown frequency or price leaves it unchanged. */
export function renewalText(template = "", frequency = "", price = "") {
  if (!template || !frequency || !price) return "";
  return template.replace("__FREQUENCY__", frequency).replace("__PRICE__", price);
}

/**
 * Keep the chosen plan in the address so a reload restores it. Shopify reads
 * ?selling_plan= on load; the variant parameter and others are left as they are.
 * Returns the new href, or null when nothing changes or the page is not this product's.
 */
export function purchaseUrl(href, productHandle, planId = "") {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!productHandle) return null;
  const handle = encodeURIComponent(productHandle).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (!new RegExp(`/products/${handle}/?$`).test(url.pathname)) return null;
  const current = url.searchParams.get("selling_plan") || "";
  const next = String(planId || "");
  if (current === next) return null;
  if (next) url.searchParams.set("selling_plan", next);
  else url.searchParams.delete("selling_plan");
  return url.toString();
}

/** Insert late-loaded choices in the same position as the server-rendered component. */
export function insertPurchaseOptionsBeforeQuantity(currentForm, incoming) {
  const buttons = currentForm?.querySelector('[ref="productFormButtons"]');
  const anchor = buttons?.querySelector('[ref="quantityLabel"]');
  if (!buttons || !anchor || anchor.parentElement !== buttons) return false;

  buttons.insertBefore(incoming.cloneNode(true), anchor);
  return true;
}

const PRODUCT_SELECT_EVENT = "shopify:product:select";
const PURCHASE_CHANGE_EVENT = "thinslim:purchase:change";
const latestInsertionRequestByTarget = new WeakMap();

function normalizeId(value) {
  return value == null || value === "" ? "" : String(value);
}

/** Track the newest variant request that may need to insert a missing component. */
export function beginPurchaseOptionsInsertion(
  eventTarget,
  variantPicker,
  requestedVariantId,
) {
  const request = {
    variantPicker,
    requestedVariantId: normalizeId(requestedVariantId),
  };
  latestInsertionRequestByTarget.set(eventTarget, request);
  return request;
}

/** Reject stale responses and responses that no longer match the selected variant. */
export function canApplyPurchaseOptionsInsertion(
  eventTarget,
  request,
  responseVariantId,
) {
  if (latestInsertionRequestByTarget.get(eventTarget) !== request) return false;

  const requestedVariantId = request.requestedVariantId;
  const currentVariantId = normalizeId(
    request.variantPicker?.selectedOption?.dataset?.variantId,
  );
  const resolvedVariantId = normalizeId(responseVariantId);

  if (
    requestedVariantId &&
    currentVariantId &&
    requestedVariantId !== currentVariantId
  )
    return false;
  if (
    requestedVariantId &&
    resolvedVariantId &&
    requestedVariantId !== resolvedVariantId
  )
    return false;
  if (
    currentVariantId &&
    resolvedVariantId &&
    currentVariantId !== resolvedVariantId
  )
    return false;

  return true;
}

class ThinSlimPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.mutationObserver?.disconnect();
    this.purchaseVisibilityObserver?.disconnect();
    if (this.syncPurchaseVisibility) document.documentElement.removeAttribute("data-tsf-purchase-in-view");
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.purchaseSelect = this.querySelector("[data-purchase-select]");
    if (this.purchaseSelect) {
      // The select remains the no-JavaScript fallback and submitted form value.
      // Once enhanced, expose only the custom radio group to assistive technology.
      this.purchaseSelect.setAttribute("aria-hidden", "true");
      this.purchaseSelect.tabIndex = -1;
    }
    this.frequencySelect = this.querySelector("[data-frequency-select]");
    this.schedule = this.querySelector("[data-subscription-schedule]");
    this.error = this.querySelector("[data-purchase-error]");
    this.productForm = this.closest("product-form-component");
    this.eventTarget = this.closest(".shopify-section, dialog");
    this.quantity = this.readQuantity();

    this.querySelectorAll("[data-purchase-choice]").forEach((choice) => {
      choice.addEventListener(
        "click",
        () => this.selectMode(choice.dataset.purchaseChoice),
        { signal },
      );
      choice.addEventListener("keydown", this.handleChoiceKeydown, { signal });
    });
    this.frequencySelect?.addEventListener(
      "change",
      () => this.selectFrequency(),
      { signal },
    );
    this.purchaseSelect?.addEventListener("change", () => this.render(), {
      signal,
    });
    this.productForm?.addEventListener(
      "quantity-selector:update",
      this.handleQuantityUpdate,
      { signal },
    );
    if (!this.closest("quick-add-dialog")) {
      this.eventTarget?.addEventListener(
        PRODUCT_SELECT_EVENT,
        this.handleProductSelect,
        { signal },
      );
    }
    this.eventTarget?.addEventListener(
      "thinslim:purchase:request",
      this.handlePurchaseRequest,
      { signal },
    );

    if (this.purchaseSelect) {
      this.mutationObserver = new MutationObserver(() => this.render());
      this.mutationObserver.observe(this.purchaseSelect, { childList: true });
    }

    if (!this.closest("quick-add-dialog")) {
      this.syncPurchaseVisibility = () => {
        const bounds = this.getBoundingClientRect();
        document.documentElement.toggleAttribute("data-tsf-purchase-in-view", bounds.bottom > 0 && bounds.top < window.innerHeight);
      };
      window.addEventListener("scroll", this.syncPurchaseVisibility, { passive: true, signal });
      window.addEventListener("resize", this.syncPurchaseVisibility, { passive: true, signal });
      this.purchaseVisibilityObserver = new IntersectionObserver(this.syncPurchaseVisibility);
      this.purchaseVisibilityObserver.observe(this);
      this.syncPurchaseVisibility();
    }
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.mutationObserver?.disconnect();
    this.purchaseVisibilityObserver?.disconnect();
    if (this.syncPurchaseVisibility) document.documentElement.removeAttribute("data-tsf-purchase-in-view");
  }

  handleQuantityUpdate = (event) => {
    if (
      event.detail?.cartLine ||
      !event.target ||
      !this.productForm?.contains(event.target)
    )
      return;
    this.quantity = this.normalizeQuantity(event.detail?.quantity);
    this.render();
  };

  handleChoiceKeydown = (event) => {
    const choices = Array.from(
      this.querySelectorAll("[data-purchase-choice]"),
    ).filter((choice) => !choice.hidden);
    const nextIndex = nextPurchaseChoiceIndex(
      choices.indexOf(event.currentTarget),
      choices.length,
      event.key,
    );
    if (nextIndex < 0) return;

    event.preventDefault();
    const nextChoice = choices[nextIndex];
    nextChoice.focus();
    this.selectMode(nextChoice.dataset.purchaseChoice);
  };

  handlePurchaseRequest = (event) => {
    const requestedProductId = String(event.detail?.productId || "");
    const currentProductId = String(this.productForm?.dataset.productId || "");
    if (
      requestedProductId &&
      currentProductId &&
      requestedProductId !== currentProductId
    )
      return;
    this.render();
  };

  normalizeQuantity(value) {
    const quantity = Number(value);
    return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
  }

  readQuantity() {
    return this.normalizeQuantity(
      this.productForm?.querySelector('input[name="quantity"]')?.value,
    );
  }

  formatMoney(minorUnits, fallback = "") {
    if (minorUnits == null || minorUnits === "") return fallback;
    const amount = Number(minorUnits);
    if (!Number.isFinite(amount)) return fallback;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency:
          this.dataset.currencyCode ||
          window.Shopify?.currency?.active ||
          "USD",
      }).format(amount / 100);
    } catch {
      return fallback;
    }
  }

  handleProductSelect = (event) => {
    const request = (this.variantRequest || 0) + 1;
    this.variantRequest = request;

    event.promise
      ?.then(({ detail }) => {
        if (
          !this.isConnected ||
          request !== this.variantRequest ||
          !detail?.html
        )
          return;
        const currentProductId = this.productForm?.dataset.productId;
        if (
          detail.productId &&
          currentProductId &&
          detail.productId !== currentProductId
        )
          return;

        const sectionId = this.productForm?.dataset.sectionId;
        const sourceForms = Array.from(
          detail.html.querySelectorAll("product-form-component"),
        );
        const sourceForm = sourceForms.find(
          (form) => !sectionId || form.dataset.sectionId === sectionId,
        );
        const incoming =
          sourceForm?.querySelector(".tsf-purchase-options") || null;
        this.syncFromIncoming(incoming, detail.resource);
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[purchase-options] Variant update failed:", error);
      });
  };

  syncFromIncoming(incoming, variant = null) {
    const currentSelect = this.purchaseSelect;
    if (!currentSelect) return;

    if (!incoming) {
      const mode = getPurchaseMode(currentSelect);
      if (mode === "one-time") {
        this.remove();
        return;
      }

      const oneTime = new Option("One-time purchase", "");
      oneTime.dataset.purchaseMode = "one-time";
      const priceCents = Number(variant?.price);
      if (
        variant?.price != null &&
        variant.price !== "" &&
        Number.isFinite(priceCents) &&
        priceCents >= 0
      ) {
        oneTime.dataset.priceCents = String(priceCents);
        oneTime.dataset.priceLabel = this.formatMoney(priceCents);
      }
      currentSelect.replaceChildren(
        new Option("Choose a purchase option", "", true, true),
        oneTime,
      );
      currentSelect.options[0].disabled = true;
      currentSelect.required = true;
      currentSelect.setCustomValidity(
        "Choose a purchase option for this size.",
      );
      const variantLabel = String(variant?.title || "");
      this.dataset.variantLabel = variantLabel === "Default Title" ? "" : variantLabel;
      this.dataset.packCount = "";
      this.dataset.purchaseUnitLabel = "";
      this.render();
      return;
    }

    const next = incoming.cloneNode(true);
    const nextSelect = next.querySelector("[data-purchase-select]");
    if (!preservePurchaseSelection(currentSelect, nextSelect)) {
      const placeholder = new Option(
        "Choose a purchase option",
        "",
        true,
        true,
      );
      placeholder.disabled = true;
      nextSelect?.prepend(placeholder);
      if (nextSelect) {
        nextSelect.selectedIndex = 0;
        nextSelect.required = true;
        nextSelect.setCustomValidity("Choose a purchase option for this size.");
      }
    }
    this.replaceWith(next);
  }

  selectMode(mode) {
    const preferredPlan = this.frequencySelect?.value || "";
    if (!choosePurchaseOption(this.purchaseSelect, mode, preferredPlan)) return;
    this.purchaseSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  selectFrequency() {
    if (
      !choosePurchaseOption(
        this.purchaseSelect,
        "subscription",
        this.frequencySelect?.value || "",
      )
    )
      return;
    this.purchaseSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  render() {
    if (!this.purchaseSelect) return;
    const planOptions = getSellingPlanOptions(this.purchaseSelect);
    const mode = getPurchaseMode(this.purchaseSelect);
    const subscriptionChoice = this.querySelector(
      '[data-purchase-choice="subscription"]',
    );
    const oneTimeChoice = this.querySelector(
      '[data-purchase-choice="one-time"]',
    );
    const hasPlans = planOptions.length > 0;

    if (subscriptionChoice) subscriptionChoice.hidden = !hasPlans;
    if (oneTimeChoice) {
      const checked = mode === "one-time";
      oneTimeChoice.setAttribute("aria-checked", String(checked));
      oneTimeChoice.tabIndex = checked ? 0 : -1;
    }
    if (subscriptionChoice) {
      const checked = mode === "subscription";
      subscriptionChoice.setAttribute("aria-checked", String(checked));
      subscriptionChoice.tabIndex = checked ? 0 : -1;
    }
    const visibleChoices = Array.from(
      this.querySelectorAll("[data-purchase-choice]"),
    ).filter((choice) => !choice.hidden);
    if (
      visibleChoices.length > 0 &&
      !visibleChoices.some((choice) => choice.getAttribute("aria-checked") === "true")
    )
      visibleChoices[0].tabIndex = 0;
    visibleChoices.forEach((choice) => {
      const indicator = choice.querySelector("[data-selection-indicator]");
      if (indicator)
        indicator.hidden = choice.getAttribute("aria-checked") !== "true";
    });
    if (this.schedule)
      this.schedule.hidden = mode !== "subscription" || !hasPlans;

    if (mode === "subscription") {
      const selectedPlan =
        this.purchaseSelect.options[this.purchaseSelect.selectedIndex];
      if (this.frequencySelect && selectedPlan?.value)
        this.frequencySelect.value = selectedPlan.value;
    }

    const previewPlan =
      mode === "subscription"
        ? this.purchaseSelect.options[this.purchaseSelect.selectedIndex]
        : planOptions.find(
            (option) => option.value === this.frequencySelect?.value,
          ) || planOptions[0];
    this.renderOneTimeValue();
    this.renderSubscriptionValue(previewPlan);
    const activeOption =
      mode === "subscription"
        ? this.purchaseSelect.options[this.purchaseSelect.selectedIndex]
        : mode === "one-time"
          ? Array.from(this.purchaseSelect.options).find(
              (option) => !option.value && !option.disabled,
            )
          : null;
    this.renderSummary(activeOption, mode);
    this.announceActivePrice(activeOption, mode);
    this.syncUrl(mode, activeOption);

    const message =
      mode === "unresolved" ? this.purchaseSelect.validationMessage : "";
    if (this.error) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  renderOneTimeValue() {
    const option = Array.from(this.purchaseSelect?.options || []).find(
      (entry) => !entry.value && !entry.disabled,
    );
    if (!option) return;
    const price =
      option.dataset.priceLabel ||
      this.formatMoney(Number(option.dataset.priceCents));
    const target = this.querySelector("[data-one-time-price]");
    if (target) target.textContent = price;
  }

  renderSubscriptionValue(option) {
    if (!option) return;
    const price =
      option.dataset.priceLabel ||
      this.formatMoney(Number(option.dataset.priceCents));
    const priceTarget = this.querySelector("[data-subscription-price]");
    const compareTarget = this.querySelector("[data-subscription-compare]");
    const oneTime = Array.from(this.purchaseSelect?.options || []).find(
      (entry) => !entry.value && !entry.disabled,
    );
    if (compareTarget) {
      // The struck price is this pack's one-time price, so the card reconciles:
      // struck − plan price = the badge for one pack.
      const compare = oneTime?.dataset.priceLabel || "";
      const compareAmount = compareTarget.querySelector?.("[data-subscription-compare-amount]") || compareTarget;
      compareAmount.textContent = compare;
      compareTarget.hidden = !compare || compare === price;
    }
    const percentTarget = this.querySelector("[data-subscription-percent]");
    const headingTarget = this.querySelector("[data-subscription-heading]");

    if (priceTarget) priceTarget.textContent = price;
    if (percentTarget) {
      const deliverySavings = quantityTotal(option.dataset.savingsCents, this.quantity);
      const badge = this.dataset.variantAvailable === "false" ? "" : subscriptionBadgeText({
        ...option.dataset,
        savingsLabel: deliverySavings == null ? option.dataset.savingsLabel : this.formatMoney(deliverySavings),
      }, this.dataset.subscriptionSavingsTemplate);
      percentTarget.textContent = badge;
      percentTarget.hidden = !badge;
    }
    if (headingTarget)
      headingTarget.textContent = subscriptionHeadingText(
        option.dataset,
        headingTarget.dataset?.subscriptionHeadingFallback || "",
      );
    this.renderRenewal(option);
  }

  /** "Renews every month at $26.59 until you cancel. …" for the previewed or chosen plan. */
  renderRenewal(option) {
    const target = this.querySelector("[data-subscription-renewal]");
    if (!target || !option) return;
    const lineCents = this.dataset.hasQuantityPriceBreaks === "true"
      ? null
      : quantityTotal(option.dataset.priceCents, this.quantity);
    const price = lineCents == null
      ? option.dataset.priceLabel || ""
      : this.formatMoney(lineCents, option.dataset.priceLabel || "");
    const text = renewalText(
      this.dataset.renewalTemplate,
      renewalFrequency(option.dataset.planName),
      price,
    );
    if (text && target.textContent !== text) target.textContent = text;
  }

  syncUrl(mode, option) {
    if (this.closest?.("quick-add-dialog") || typeof window === "undefined" || !window.history?.replaceState) return;
    const planId = mode === "subscription" ? String(option?.value || "") : "";
    const next = purchaseUrl(window.location.href, this.dataset.productHandle, planId);
    if (!next) return;
    try {
      window.history.replaceState(window.history.state, "", next);
    } catch {
      // A blocked history update must not break the purchase choice.
    }
  }

  renderSummary(option, mode) {
    const summary = purchaseSummary(option, this.quantity, {
      mode,
      variantLabel: this.dataset.variantLabel || "",
      packCount: this.dataset.packCount,
      unitNoun: this.dataset.unitNoun,
      unitPlural: this.dataset.unitPlural,
      packContents: this.dataset.packContents,
      hasQuantityPriceBreaks: this.dataset.hasQuantityPriceBreaks === "true",
      baselineTotalCents: this.dataset.baselineTotalCents,
      available: this.dataset.variantAvailable !== "false",
    });
    const detailTarget = this.querySelector("[data-purchase-summary-detail]");
    const totalTarget = this.querySelector("[data-purchase-summary-total]");
    const summaryBox = this.querySelector("[data-purchase-summary]");
    // A single unit repeats the price shown above, so the total only appears
    // once the shopper raises the quantity.
    if (summaryBox) summaryBox.hidden = Boolean(summary) && this.quantity === 1;
    if (!summary) {
      if (detailTarget) detailTarget.textContent = "Choose a purchase option.";
      if (totalTarget) totalTarget.textContent = "";
      return;
    }

    if (detailTarget) detailTarget.textContent = summary.detail;
    if (totalTarget) {
      if (summary.totalCents == null) {
        totalTarget.textContent = "Your cart shows the total with quantity pricing.";
        return;
      }
      const label = mode === "subscription" ? " per delivery" : "";
      const savings = summary.savingsCents ? this.formatMoney(summary.savingsCents) : "";
      const savingText = savings
        ? summary.includesPackSavings
          ? this.dataset.totalSavingsTemplate?.replace("__AMOUNT__", savings) || `You save ${savings} total`
          : `You save ${savings}`
        : "";
      totalTarget.textContent = `${this.formatMoney(summary.totalCents)}${label}${savingText ? ` · ${savingText}` : ""}`;
    }
  }

  announceActivePrice(option, mode) {
    const isResolved = option && mode !== "unresolved";
    this.dispatchEvent(
      new CustomEvent(PURCHASE_CHANGE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          productId: String(this.productForm?.dataset.productId || ""),
          variantId: isResolved ? String(this.dataset.variantId || "") : "",
          mode: isResolved ? mode : "unresolved",
          priceCents: isResolved ? String(option.dataset.priceCents || "") : "",
          priceLabel: isResolved ? String(option.dataset.priceLabel || "") : "",
          priceBasis: isResolved ? this.purchaseUnitLabel() : "",
          planId: isResolved && mode === "subscription" ? String(option.value || "") : "",
          baselineTotalCents: isResolved ? String(this.dataset.baselineTotalCents || "") : "",
          oneTimeCents: isResolved ? String(Array.from(this.purchaseSelect?.options || []).find(
            (entry) => !entry.value && !entry.disabled,
          )?.dataset.priceCents || "") : "",
          quantity: String(this.quantity || 1),
        },
      }),
    );
  }

  purchaseUnitLabel() {
    const contents = packContentsFromData(this.dataset.packContents);
    if (contents) return packContentsLabel(contents);
    if (this.dataset.purchaseUnitLabel) return this.dataset.purchaseUnitLabel;
    const packCount = Number(this.dataset.packCount);
    if (!Number.isInteger(packCount) || packCount < 1) return "per item";
    const noun = packCount === 1 ? this.dataset.unitNoun : this.dataset.unitPlural;
    return `per ${packCount === 1 ? "" : `${packCount} `}${noun || "item"}`;
  }
}

if (!customElements.get("thinslim-purchase-options")) {
  customElements.define("thinslim-purchase-options", ThinSlimPurchaseOptions);
}

// A variant with no plans renders no component. This listener can add the component
// if a later variant has plans. Quick Buy has its own selection-preserving sync.
if (typeof document !== "undefined")
  document.addEventListener(PRODUCT_SELECT_EVENT, (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest("quick-add-dialog")
    )
      return;
    const eventTarget = event.target.closest(".shopify-section, dialog");
    if (!eventTarget) return;
    const variantPicker = event.target.closest("variant-picker");
    const productId = variantPicker?.dataset.productId;
    const insertionRequest = beginPurchaseOptionsInsertion(
      eventTarget,
      variantPicker,
      event.detail?.variantId ||
        variantPicker?.selectedOption?.dataset.variantId,
    );
    if (eventTarget.querySelector(".tsf-purchase-options")) return;

    event.promise
      ?.then(({ detail, variant }) => {
        if (
          !canApplyPurchaseOptionsInsertion(
            eventTarget,
            insertionRequest,
            detail?.resource?.id || variant?.id,
          ) ||
          !detail?.html ||
          eventTarget.querySelector(".tsf-purchase-options")
        )
          return;
        const currentForms = Array.from(
          eventTarget.querySelectorAll("product-form-component"),
        );
        const currentForm = currentForms.find(
          (form) => !productId || form.dataset.productId === productId,
        );
        if (!currentForm) return;

        const sectionId = currentForm.dataset.sectionId;
        const sourceForms = Array.from(
          detail.html.querySelectorAll("product-form-component"),
        );
        const sourceForm = sourceForms.find(
          (form) => !sectionId || form.dataset.sectionId === sectionId,
        );
        const incoming = sourceForm?.querySelector(".tsf-purchase-options");
        if (incoming) insertPurchaseOptionsBeforeQuantity(currentForm, incoming);
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[purchase-options] Variant insertion failed:", error);
      });
  });
